import { releaseSpec } from './gottiRelease';
import { sanitizePackageName } from './packageName';
import { moduleSpecifier, quoted, type TopologyPaths } from './topology';

/** The ports the scaffold's servers listen on: gotti.config.ts and the web client entry read the same ones. */
export const SCAFFOLD_PORTS = Object.freeze({
  webClient: 8080,
  master: 3030,
  masterWeb: 3031,
  gate: 3032,
  gateWeb: 3033,
  connector: 5050,
  connectorWeb: 5051,
  area: 6060,
  starter: 9000,
});
const PORTS = SCAFFOLD_PORTS;

/**
 * The root files `gotti playground` and `gotti dev` need, and the web client `gotti dev` serves — at
 * `paths.clientEntry`, the file `index.html` loads.
 */
export function emitScaffoldFiles(options: { name: string; paths: TopologyPaths }): Record<string, string> {
  const { paths } = options;
  const name = sanitizePackageName(options.name);
  const root = (to: string) => quoted(moduleSpecifier('sandbox.playground.ts', to));
  const fromClientEntry = (to: string) => quoted(moduleSpecifier(paths.clientEntry, to));

  const packageJson = {
    name,
    version: '0.0.1',
    private: true,
    type: 'module',
    scripts: {
      playground: 'gotti playground --ui',
      test: 'gotti playground --test --headless',
      build: 'vite build',
      start: 'npm run build && gotti dev',
    },
    // The gotti packages are not on npm yet: a project installs them from their GitHub release (`gottiRelease.ts`).
    dependencies: Object.fromEntries(['@gotti/core', '@gotti/client', '@gotti/server', '@gotti/deployer'].map((name) => [name, releaseSpec(name)])),
    devDependencies: {
      '@gotti/cli': releaseSpec('@gotti/cli'),
      '@gotti/playground': releaseSpec('@gotti/playground'),
      '@gotti/test-utils': releaseSpec('@gotti/test-utils'),
      '@types/node': '^24.3.0',
      playwright: '^1.54.1',
      typescript: '^5.4.2',
      // The range the gotti CLI and playground declare as their peer: npm refuses a project whose vite conflicts with it.
      vite: '^6.0.0',
    },
  };

  const tsconfig = {
    compilerOptions: {
      target: 'ES2022', module: 'ESNext', moduleResolution: 'bundler',
      strict: false, esModuleInterop: true, skipLibCheck: true,
      forceConsistentCasingInFileNames: true, lib: ['es2021', 'dom'], types: ['node'],
      baseUrl: '.', paths: { '@/*': ['./src/*'] },
    },
    include: ['src/**/*.ts', '*.ts'],
    exclude: ['node_modules', 'dist'],
  };

  return {
    'package.json': JSON.stringify(packageJson, null, 2) + '\n',
    'tsconfig.json': JSON.stringify(tsconfig, null, 2) + '\n',
    'gotti.config.ts': [
      `import { defineConfig } from '@gotti/deployer';`,
      '',
      '// Where the servers run: this computer, or — where a deploy runs the game — the server\'s own address (GOTTI_HOST).',
      '// Read through globalThis: the sandbox\'s editor checks this file without Node\'s types.',
      `const env = (globalThis as { process?: { env: Record<string, string | undefined> } }).process?.env ?? {};`,
      `const host = env.GOTTI_HOST || '127.0.0.1';`,
      '',
      '// One game, one area: the topology the sandbox runs. The gate and master are the framework defaults — this project',
      '// has no gate or master file of its own.',
      'export default defineConfig({',
      `  games: [{ id: 'sandbox-game', gameFilePath: ${quoted(paths.serverGame)} }],`,
      '  servers: {',
      `    webClient: { clientFileName: 'index.html', host, port: ${PORTS.webClient}, publicPath: 'dist/client/', clientFilePath: 'dist/client/index.html' },`,
      `    master: { id: 'master', host, port: ${PORTS.master}, webPort: ${PORTS.masterWeb} },`,
      `    gate: { id: 'gate', host, port: ${PORTS.gate}, webPort: ${PORTS.gateWeb} },`,
      `    connectors: [{ id: 'connector-1', host, port: ${PORTS.connector}, webPort: ${PORTS.connectorWeb}, game: 'sandbox-game' }],`,
      `    areas: [{ id: 'sandbox-area-1', host, port: ${PORTS.area}, areaFilePath: ${quoted(paths.serverArea)}, gameId: 'sandbox-game' }],`,
      '  },',
      `  starter: { port: ${PORTS.starter} },`,
      '});',
      '',
    ].join('\n'),
    'playground.config.ts': [
      `import { definePlaygroundConfig } from '@gotti/playground';`,
      '',
      'export default definePlaygroundConfig({',
      `  playgrounds: ['./sandbox.playground.ts'],`,
      '  // The live sandbox preview simulates 50 ms between each client and the server.',
      '  mockConfig: { clientServerLatency: 50, serverServerLatency: 0 },',
      '});',
      '',
    ].join('\n'),
    'sandbox.playground.ts': [
      `import { definePlayground } from '@gotti/playground';`,
      `import ClientSandboxGame from ${root(paths.clientGame)};`,
      `import { SandboxArea as ClientSandboxArea } from ${root(paths.clientArea)};`,
      `import ServerSandboxGame from ${root(paths.serverGame)};`,
      `import { SandboxArea as ServerSandboxArea } from ${root(paths.serverArea)};`,
      `import { SANDBOX_AREA_ID, SANDBOX_GAME_ID } from ${root(paths.shared)};`,
      '',
      "// The game as the sandbox runs it: one area, clients joining as player-N. Add a `scenario` to test it.",
      'export default definePlayground({',
      `  title: 'Sandbox',`,
      `  description: 'The game as the sandbox runs it: one area, clients joining as player-N.',`,
      '  environment: {',
      '    clientGame: ClientSandboxGame,',
      '    serverGame: { gameFile: ServerSandboxGame, id: SANDBOX_GAME_ID },',
      '    clientAreas: [ClientSandboxArea],',
      '    serverAreas: [{ areaFile: ServerSandboxArea, id: SANDBOX_AREA_ID }],',
      '  },',
      '  clientConfig: {',
      '    numberOfClients: 1,',
      '    clientAuth: ({ index }) => ({ id: `player-${index + 1}`, username: `Player ${index + 1}` }),',
      '    joinGame: { gameId: SANDBOX_GAME_ID, initialAreas: [SANDBOX_AREA_ID] },',
      '  },',
      '});',
      '',
    ].join('\n'),
    'index.html': [
      '<!DOCTYPE html>',
      '<html lang="en">',
      '<head>',
      '  <meta charset="UTF-8">',
      '  <meta name="viewport" content="width=device-width, initial-scale=1.0">',
      `  <title>${name}</title>`,
      '</head>',
      '<body>',
      '  <div id="app"></div>',
      `  <script type="module" src="/${paths.clientEntry}"></script>`,
      '</body>',
      '</html>',
      '',
    ].join('\n'),
    'vite.config.ts': [
      `import { defineConfig } from 'vite';`,
      `import path from 'path';`,
      '',
      'export default defineConfig({',
      '  root: __dirname,',
      `  server: { port: 4811, host: 'localhost' },`,
      '  build: {',
      `    outDir: './dist/client',`,
      '    emptyOutDir: true,',
      `    rollupOptions: { input: { main: path.resolve(__dirname, 'index.html') } },`,
      '  },',
      `  resolve: { alias: { '@': path.resolve(__dirname, './src') } },`,
      '});',
      '',
    ].join('\n'),
    [paths.clientEntry]: [
      '// The web client `gotti dev` serves: one canvas, one client, joining the game through the gate.',
      `import { Client } from '@gotti/client';`,
      `import SandboxGame from ${fromClientEntry(paths.clientGame)};`,
      `import { SandboxArea } from ${fromClientEntry(paths.clientArea)};`,
      `import { SANDBOX_GAME_ID } from ${fromClientEntry(paths.shared)};`,
      '',
      `const container = document.getElementById('app') ?? document.body;`,
      `const canvas = document.createElement('canvas');`,
      'canvas.width = 300;',
      'canvas.height = 300;',
      'container.appendChild(canvas);',
      '',
      '// The gate is on the host that served this page — a deployed server, or this computer under `gotti dev` — at the',
      '// gate web port gotti.config.ts gives it. A host without a page address (a native console) sets GOTTI_GATE_URL first.',
      `const gateServerURL = (globalThis as { GOTTI_GATE_URL?: string }).GOTTI_GATE_URL ?? \`\${location.protocol}//\${location.hostname}:${PORTS.gateWeb}\`;`,
      '',
      'async function main() {',
      '  const client = new Client(',
      '    { gateServerURL, requestTimeout: 10000, failedPeerRequestTrackerTimeout: 15000, useWebRtcConnector: false },',
      '    undefined,',
      '    { canvas, container },',
      '  );',
      '  client.addGameFile(SandboxGame);',
      '  client.addAreaFile(SandboxArea);',
      '  await client.authenticate({});',
      '  await client.joinGame(SANDBOX_GAME_ID);',
      '}',
      '',
      'main().catch((err) => console.error(err));',
      '',
    ].join('\n'),
  };
}
